/**
 * Pure, timezone-correct calendar-day arithmetic for the Month/Week/Day
 * views. No React, no I/O — every function takes its inputs explicitly
 * (including the IANA zone) so it can be unit-tested against any viewer
 * timezone regardless of the machine actually running the tests.
 *
 * Design Q3: events are stored as UTC instants (`startsAt`/`endsAt`, plus the
 * `timeZone` they were created in) and rendered in the VIEWER's zone. This
 * module owns that render-time conversion. `CalendarScreen.tsx` used to
 * bucket days with `isoDate.slice(0, 10)` — the UTC calendar day baked into
 * the ISO string — which silently misfiles any event whose local day differs
 * from its UTC day. `localDayKey` (and everything built on it) replaces that.
 *
 * A "local day" is represented as a plain `{ year, month, day }` object
 * (`LocalDate`, month 1-12) rather than a `Date` instance: a `Date` is a
 * single instant, not a calendar day, and there is no instant that
 * unambiguously means "August 10th" across zones. Calendar arithmetic
 * (`addDays`, `dayOfWeek`, grid generation) is done on these plain values via
 * a UTC-anchored scratch `Date` purely as a leap-year-aware day-counter — it
 * never represents a real moment in time.
 */

export interface LocalDate {
  year: number;
  month: number; // 1-12
  day: number;
}

interface LocalDateTime extends LocalDate {
  hour: number; // 0-23
  minute: number;
}

export function getViewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function partsInZone(isoDateTime: string, timeZone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(isoDateTime));
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

export function toLocalDate(isoDateTime: string, timeZone: string): LocalDate {
  const parts = partsInZone(isoDateTime, timeZone);
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}

export function toLocalDateTime(isoDateTime: string, timeZone: string): LocalDateTime {
  const parts = partsInZone(isoDateTime, timeZone);
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
}

export function localDateKey(date: LocalDate): string {
  const m = String(date.month).padStart(2, '0');
  const d = String(date.day).padStart(2, '0');
  return `${date.year}-${m}-${d}`;
}

export function localDayKey(isoDateTime: string, timeZone: string): string {
  return localDateKey(toLocalDate(isoDateTime, timeZone));
}

export function isSameLocalDay(a: string, b: string, timeZone: string): boolean {
  return localDayKey(a, timeZone) === localDayKey(b, timeZone);
}

export function isToday(date: LocalDate, timeZone: string, now: Date = new Date()): boolean {
  return localDateKey(date) === localDayKey(now.toISOString(), timeZone);
}

export function compareLocalDate(a: LocalDate, b: LocalDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

// A UTC-anchored Date used purely as a day-counter (leap years, month
// lengths) — never surfaced as, or read back as, a real instant.
function toUtcAnchor(date: LocalDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day));
}

function fromUtcAnchor(date: Date): LocalDate {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

export function addDays(date: LocalDate, amount: number): LocalDate {
  const anchor = toUtcAnchor(date);
  anchor.setUTCDate(anchor.getUTCDate() + amount);
  return fromUtcAnchor(anchor);
}

/** 0 = Sunday, 6 = Saturday — matches `Date#getDay`. */
export function dayOfWeek(date: LocalDate): number {
  return toUtcAnchor(date).getUTCDay();
}

/**
 * Adds calendar months, clamping the day when the target month is shorter
 * (Jan 31 + 1 month lands on Feb 28/29, never an overflowed "Feb 31").
 */
export function addMonths(date: LocalDate, amount: number): LocalDate {
  const totalMonths = date.month - 1 + amount;
  const year = date.year + Math.floor(totalMonths / 12);
  const month = (((totalMonths % 12) + 12) % 12) + 1;
  const daysInTargetMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { year, month, day: Math.min(date.day, daysInTargetMonth) };
}

export function fromNativeDate(date: Date): LocalDate {
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

/**
 * Builds a `Date` at local midnight of `date` in the *runtime's* own zone —
 * safe to hand to `date-fns` formatters, which is the only reason this
 * exists. Only meaningful when the runtime's zone is the viewer's zone
 * (true for every real device; a test injecting a different `timeZone` into
 * the functions above should assert on the `LocalDate` fields, not this).
 */
export function toNativeDate(date: LocalDate): Date {
  return new Date(date.year, date.month - 1, date.day);
}

/**
 * The 7xN grid (5 or 6 week rows) for a given month, including the leading
 * days from the previous month and trailing days from the next month needed
 * to fill the first and last week.
 */
export function getMonthGridRange(
  year: number,
  month: number,
  weekStartsOn: 0 | 1 = 0,
): LocalDate[][] {
  const firstOfMonth: LocalDate = { year, month, day: 1 };
  const leadingDays = (dayOfWeek(firstOfMonth) - weekStartsOn + 7) % 7;
  const gridStart = addDays(firstOfMonth, -leadingDays);

  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastOfMonth: LocalDate = { year, month, day: daysInMonth };
  const trailingDays = (weekStartsOn + 6 - dayOfWeek(lastOfMonth) + 7) % 7;
  const gridEnd = addDays(lastOfMonth, trailingDays);

  const weeks: LocalDate[][] = [];
  let cursor = gridStart;
  while (compareLocalDate(cursor, gridEnd) <= 0) {
    const week: LocalDate[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

/** The 7 days of the week containing `date`, honouring `weekStartsOn`. */
export function getWeekRange(date: LocalDate, weekStartsOn: 0 | 1 = 0): LocalDate[] {
  const diff = (dayOfWeek(date) - weekStartsOn + 7) % 7;
  const start = addDays(date, -diff);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** The single-day range for the Day view — kept as a function for symmetry
 * with `getWeekRange`/`getMonthGridRange` so call sites don't special-case it. */
export function getDayRange(date: LocalDate): LocalDate[] {
  return [date];
}

const LOCAL_DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Inverse of `localDateKey`; null for anything that is not a real date in key form. */
export function parseLocalDateKey(key: unknown): LocalDate | null {
  if (typeof key !== 'string') return null;
  const match = LOCAL_DATE_KEY.exec(key);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}
